import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { API_BASE } from "@/lib/api";
import { useAudioSettings } from "@/lib/audio-settings";
import { nextSound, SOUND_ORDER, PHASE_TO_SOUND_KEY } from "@/lib/sound-order";
import { cabinNote } from "@/lib/cabin-note";
import { playClip, setCabinLevel } from "@/lib/cabin-audio";
import { savedFlight, recordSoundReached } from "@/lib/flight-session";

/**
 * SoundPlayer
 * -----------
 * Watches for `phase` prop changes. On each new phase, looks up the current
 * flight's operator soundpack and plays the matching audio clip once, through
 * the app-wide cabin channel (lib/cabin-audio.js) so no page change cuts it off.
 *
 * Volume + mute come from `useAudioSettings` (persisted to localStorage), so
 * the volume slider in the sidebar controls playback in real time.
 */
export default function SoundPlayer({ phase, operator, flightId = null, enabled = true }) {
  const lastPhaseRef = useRef(null);
  const reachedRef = useRef(-1);   // furthest SOUND_ORDER index this flight has reached
  // API_BASE, not the raw env var. Unset — which is the normal same-origin
  // deployment — `process.env.REACT_APP_BACKEND_URL` is undefined and this
  // built "undefined/api/sounds/file/x.mp3", so no clip has ever played in
  // the browser build. api.js normalises this to "" for exactly this reason;
  // rebuilding the base by hand walked straight back into it.
  const backendBase = API_BASE;
  const [{ volume, muted }] = useAudioSettings();
  const levelRef = useRef({ volume, muted });   // for the log line, without replaying on a volume change
  levelRef.current = { volume, muted };

  // Per-phase auto-trigger switches (migration 009). Absent or unset means on,
  // so a pilot who has never opened the Audio page hears what they always did,
  // and a phase added to sounds.PHASES later is not silently muted for anyone.
  const { data: prefs } = useQuery({
    queryKey: ["pilot-prefs"],
    queryFn: () => api.get("/pilot/prefs").then((r) => r.data),
    staleTime: 60_000,
  });

  const { data: pack, isSuccess: packAnswered } = useQuery({
    queryKey: ["operator-pack", operator],
    queryFn: () => (operator ? api.get(`/sounds/packs/for-operator/${operator}`).then((r) => r.data) : null),
    enabled: !!operator,
    staleTime: 60_000,
  });

  // A new flight starts from no phase, so its BOARDING plays even when the
  // last flight also began at BOARDING. A flight already under way (VACARS5
  // restarted, or updated, mid-flight) carries on from the announcements it
  // has had: this was -1, so a restart in cruise replayed the in-flight one
  // (beta report 2026-10-18). Nothing playing is stopped here.
  useEffect(() => {
    lastPhaseRef.current = null;
    reachedRef.current = flightId == null ? -1 : savedFlight(flightId).reached;
    if (reachedRef.current >= 0) {
      cabinNote(`announcements resume after ${SOUND_ORDER[reachedRef.current]} (flight ${flightId})`);
    }
  }, [flightId]);

  // An operator with no active pack plays nothing; say so once per operator.
  const packMissingNoted = useRef(null);
  useEffect(() => {
    if (!operator || !packAnswered || pack || packMissingNoted.current === operator) return;
    packMissingNoted.current = operator;
    cabinNote(`no active soundpack for operator ${operator}: no announcements will play`);
  }, [operator, pack, packAnswered]);

  // Live-adjust volume/mute on the clip that is playing. The channel is shared
  // with the Audio page's soundboard (lib/cabin-audio.js); this component is
  // always mounted, so it keeps the level in step for both.
  useEffect(() => {
    setCabinLevel({ volume, muted });
  }, [volume, muted]);

  useEffect(() => {
    if (!enabled || !phase || !pack) return;
    if (phase === lastPhaseRef.current) return;
    lastPhaseRef.current = phase;
    const { key, reached } = nextSound(phase, reachedRef.current);
    if (!key) {
      // CLIMB, TAKEOFF (no clip), or a step back. Only a phase that has a clip
      // is worth a line: it says why that clip did not play again.
      const wanted = PHASE_TO_SOUND_KEY[phase] || phase;
      if (SOUND_ORDER.includes(wanted)) {
        cabinNote(`${wanted} not played: the flight is already past it (furthest: ${SOUND_ORDER[reachedRef.current] || "none"})`);
      }
      return;
    }
    reachedRef.current = reached;
    recordSoundReached(flightId, reached);
    const op = pack.operator || operator;
    if (prefs?.sound_auto?.[key] === false) { cabinNote(`${key} not played: switched off on the Audio page`); return; }
    const meta = pack.sounds?.[key];
    if (!meta?.filename) { cabinNote(`${key} not played: the ${op} pack has no clip for it`); return; }
    const url = `${backendBase}/api/sounds/file/${meta.filename}`;
    const { volume: vol, muted: mute } = levelRef.current;
    const level = mute ? "MUTED" : `volume ${Math.round((Number(vol) || 0) * 100)}%`;
    playClip(url, key).then(
      () => cabinNote(`${key} playing (${op} pack, ${level}, phase ${phase})`),
      (e) => cabinNote(`${key} could not play: ${e?.name || "error"} ${e?.message || ""}`.trim()),
    );
  }, [phase, pack, enabled, backendBase, prefs, flightId, operator]);

  return null;
}
